import { useId } from "react";

import { Anchor, Group, Radio, Stack, Text, type TextProps } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import {
  type ClearableRadioOption,
  yesNoRadioOptions,
} from "@/components/ClearableYesNoRadioField/constants/yes-no-radio-options";
import { slugify } from "@/utils/strings";

/** Sets the radio form field to null. */
function clearRadioFormFieldValue<
  T extends Record<K, string | null | undefined>,
  K extends keyof T & string,
>(form: UseFormReturnType<T>, name: K): void {
  (form.setFieldValue as (path: K, value: null) => void)(name, null);
}

export interface ClearableYesNoRadioFieldProps<
  T extends Record<K, string | null | undefined>,
  K extends keyof T & string,
> {
  form: UseFormReturnType<T>;
  name: K;
  label: string;
  /** Called after the selection is cleared (for example to reset dependent fields). */
  onClear?: () => void;
  dataPath?: string;
  /** Stable test id for the field; option radios use `${dataTestId}-${slugifiedLabel}`. */
  dataTestId?: string;
  options?: readonly ClearableRadioOption[];
  labelSize?: TextProps["size"];
  labelFw?: TextProps["fw"];
}

/** Yes/No radio group with a Clear control next to the field label. */
export function ClearableYesNoRadioField<
  T extends Record<K, string | null | undefined>,
  K extends keyof T & string,
>({
  form,
  name,
  label,
  onClear,
  dataPath,
  dataTestId,
  options = yesNoRadioOptions,
  labelSize = "md",
  labelFw = 500,
}: ClearableYesNoRadioFieldProps<T, K>) {
  const labelId = useId();
  const currentValue = form.values[name];
  const hasSelection =
    typeof currentValue === "string" && options.some((option) => option.value === currentValue);

  const handleClear = () => {
    clearRadioFormFieldValue(form, name);
    onClear?.();
  };

  const radioInputProps = form.getInputProps(name);

  let clearTestId: string | undefined;
  if (dataTestId) {
    clearTestId = `${dataTestId}-clear`;
  } else if (dataPath) {
    clearTestId = `${dataPath}-clear`;
  }

  return (
    <Stack gap="xs" data-path={dataPath} data-testid={dataTestId}>
      <Group gap="sm" wrap="nowrap" align="center" justify="space-between">
        <Text id={labelId} fw={labelFw} size={labelSize} style={{ flex: 1 }}>
          {label}
        </Text>
        <Anchor
          type="button"
          c="burgundy.9"
          td="underline"
          size="compact-xs"
          component="button"
          variant="transparent"
          onClick={handleClear}
          disabled={!hasSelection}
          style={{ flexShrink: 0 }}
          data-testid={clearTestId}
        >
          Clear
        </Anchor>
      </Group>
      <Radio.Group {...radioInputProps} aria-labelledby={labelId}>
        <Group mt="sm">
          {options.map((option) => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
              data-testid={dataTestId ? `${dataTestId}-${slugify(option.label)}` : undefined}
            />
          ))}
        </Group>
      </Radio.Group>
    </Stack>
  );
}
